import React, { useState, memo } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CheckCircle2, MapPin, Heart, Users, Briefcase, Megaphone, TrendingUp, ChevronDown, ChevronUp, Award, Clock, Shield, FileText, ArrowRight, CheckSquare } from 'lucide-react';
import { sendViaWeb3Forms } from '../services/web3formsService';
import { saveSubmission, type BenevoleSubmission } from '../services/databaseService';
export function BenevolesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [openFiche, setOpenFiche] = useState<string | null>(null);
  const [charterAccepted, setCharterAccepted] = useState(false);
  // --- DATA ---
  const POLES = [{
    id: 'terrain',
    title: 'Terrain & événements',
    desc: 'Mobilisation, accueil et logistique sur le terrain',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    icon: Users,
    roles: ['Acteur terrain & events', 'Hôte / Hôtesse', 'Coursier(ère)']
  }, {
    id: 'admin',
    title: 'Administration & appui',
    desc: 'Gestion administrative, financière et juridique',
    color: 'bg-green-50 border-green-200 text-green-700',
    icon: FileText,
    roles: ['Assistant(e) administratif(ve)', 'Comptable', 'Juriste']
  }, {
    id: 'comms',
    title: 'Communication & image',
    desc: "Création de contenu et visibilité de l'association",
    color: 'bg-purple-50 border-purple-200 text-purple-700',
    icon: Megaphone,
    roles: ['Créateur(trice) contenu', 'Photographe', 'Graphic designer']
  }, {
    id: 'dev',
    title: 'Développement & insertion',
    desc: 'Accompagnement des bénéficiaires et développement',
    color: 'bg-amber-50 border-amber-200 text-amber-700',
    icon: TrendingUp,
    roles: ['Accompagnateur(trice) insertion', 'Technico-commercial(e)', 'Commercial(e) / Qualiticien(ne)']
  }];
  const FICHES = [{
    role: 'Accompagnateur(trice) insertion',
    pole: 'Développement & insertion',
    mission: "Guider les bénéficiaires à chaque étape de leur parcours, avant et après les ateliers de renforcement de capacités, jusqu'à leur pleine intégration professionnelle.",
    responsabilites: ['Pré-insertion : accueil, évaluation des besoins, orientation', 'Préparation des bénéficiaires aux ateliers', "Post-insertion : accompagnement individualisé après les ateliers jusqu'à l'emploi", 'Aide à la rédaction de CV et lettres de motivation', "Mise en relation avec le réseau d'employeurs", 'Suivi régulier des bénéficiaires placés', 'Contribution à la lutte contre les abus'],
    profil: "Empathie, écoute active et patience. Excellent relationnel. Connaissance du marché de l'emploi au Bénin. Une expérience en RH, travail social ou coaching est un plus.",
    dispo: 'Régulière (quelques heures par semaine)'
  }, {
    role: 'Commercial(e) / Qualiticien(ne)',
    pole: 'Développement & insertion',
    mission: "Garantir la qualité des placements et développer le réseau d'employeurs partenaires.",
    responsabilites: ['Prospection de nouveaux employeurs partenaires', 'Évaluation des conditions de travail chez les employeurs', 'Suivi de la satisfaction des bénéficiaires', 'Médiation en cas de conflit'],
    profil: 'Formation commerciale ou qualité. Force de persuasion. Rigueur et diplomatie.',
    dispo: 'Régulière'
  }, {
    role: 'Assistant(e) administratif(ve)',
    pole: 'Administration & appui',
    mission: "Appuyer le bureau exécutif dans la gestion quotidienne de l'association.",
    responsabilites: ['Gestion des courriels et appels', 'Mise à jour des bases de données bénéficiaires', 'Préparation des comptes-rendus de réunions', 'Appui logistique pour les ateliers'],
    profil: 'Organisé(e), rigoureux(se), maîtrise des outils bureautiques (Word, Excel).',
    dispo: 'Flexible'
  }, {
    role: 'Comptable',
    pole: 'Administration & appui',
    mission: "Assurer la transparence et la rigueur financière de l'association.",
    responsabilites: ['Tenue de la comptabilité', 'Suivi des budgets et dépenses', 'Préparation des rapports financiers', 'Appui aux demandes de subventions'],
    profil: 'Formation en comptabilité/finances. Honnêteté et rigueur.',
    dispo: 'Flexible'
  }, {
    role: 'Coursier(ère)',
    pole: 'Terrain & événements',
    mission: "Faciliter les démarches physiques de l'association dans Cotonou.",
    responsabilites: ['Dépôt de courriers officiels', 'Achat de fournitures pour les ateliers', 'Appui logistique lors des événements'],
    profil: 'Ponctualité, fiabilité et sens du service. Bonne connaissance de Cotonou. Disponibilité ponctuelle. Adhésion aux valeurs ADCC.',
    dispo: 'Ponctuelle'
  }, {
    role: 'Acteur terrain & events',
    pole: 'Terrain & événements',
    mission: "Appui opérationnel lors des déploiements et événements de l'association.",
    responsabilites: ['Installation logistique', 'Accueil du public', 'Gestion des flux lors des événements', 'Appui aux formateurs'],
    profil: 'Dynamique, proactif(ve) et aimant le contact humain.',
    dispo: 'Ponctuelle (lors des événements)'
  }, {
    role: 'Hôte / Hôtesse',
    pole: 'Terrain & événements',
    mission: 'Accueil et orientation du public lors de nos événements et ateliers.',
    responsabilites: ['Accueil physique', 'Émargement des participants', 'Orientation et information', 'Remise de documents'],
    profil: 'Souriant(e), excellente présentation, sens du service et courtoisie.',
    dispo: 'Ponctuelle'
  }, {
    role: 'Juriste',
    pole: 'Administration & appui',
    mission: 'Votre expertise juridique protège ADCC et ses bénéficiaires. Dans une association engagée pour la dignité au travail et la lutte contre les abus, votre rôle est stratégique pour sécuriser toutes nos actions.',
    responsabilites: ["Conseil juridique au Bureau exécutif sur les activités de l'association", 'Rédaction et vérification de contrats, conventions et partenariats', 'Appui aux actions de sensibilisation aux droits des travailleurs/ses', 'Veille sur le droit associatif, le droit du travail et la protection sociale au Bénin', "Assistance lors des déclarations et obligations légales de l'association", "Soutien aux bénéficiaires confrontés à des situations d'abus ou de violence au travail"],
    profil: 'Formation en droit (licence minimum). Connaissance du droit béninois et du droit associatif appréciée. Capacité à vulgariser les concepts juridiques. Sensibilité aux questions de genre et de droits des travailleuses.',
    dispo: 'Flexible'
  }, {
    role: 'Créateur(trice) contenu',
    pole: 'Communication & image',
    mission: "Vous êtes la voix engagée d'ADCC ! Par vos mots et vos idées, vous sensibilisez le grand public à l'autonomisation des femmes et des jeunes, valorisez les parcours des bénéficiaires et renforcez la visibilité de l'association.",
    responsabilites: ["Rédaction de posts, articles et newsletters valorisant les missions d'ADCC", 'Création de contenu engagé sur les réseaux sociaux (Facebook, Instagram, LinkedIn…)', 'Mise en valeur des témoignages et parcours de bénéficiaires (dans le respect de leur intimité)', 'Sensibilisation aux droits des travailleur·ses et à la lutte contre les abus', "Mise à jour et animation du site internet de l'association", 'Suivi des statistiques de publication et adaptation de la stratégie éditoriale'],
    profil: "Excellentes qualités rédactionnelles en français. Sensibilité aux questions sociales, de genre et d'insertion professionnelle. Créativité, sens de la narration et de la pédagogie. Connaissance des réseaux sociaux et des outils de publication en ligne.",
    dispo: 'Flexible'
  }, {
    role: 'Photographe',
    pole: 'Communication & image',
    mission: "À travers votre objectif, vous capturez l'essence d'ADCC : les visages déterminés, les compétences qui s'affirment, les parcours qui transforment. Vos images sont autant de témoignages de l'impact de l'association.",
    responsabilites: ["Couverture photo des ateliers, formations et événements d'ADCC", 'Portraits valorisants des bénéficiaires, bénévoles et partenaires (avec consentement)', 'Retouche et sélection des photographies pour les différents supports', "Alimentation et organisation de la photothèque de l'association", "Fourniture d'images pour le site internet, les réseaux sociaux et les rapports annuels"],
    profil: "Maîtrise de la photographie (appareil numérique reflex ou hybride). Connaissance des logiciels de retouche (Lightroom, Photoshop ou équivalent). Sens humain, respect et discrétion vis-à-vis des bénéficiaires. Disponibilité lors des événements ponctuels de l'association.",
    dispo: 'Ponctuelle'
  }, {
    role: 'Graphic designer',
    pole: 'Communication & image',
    mission: "Vous donnez un visage à ADCC ! Votre créativité traduit visuellement les valeurs de dignité, compétence et création de l'association, rendant nos messages accessibles, attractifs et mémorables pour tous les publics.",
    responsabilites: ['Création de visuels pour les réseaux sociaux, le web et les campagnes de communication', 'Conception de flyers, affiches et supports pour les ateliers et événements', "Respect et développement de la charte graphique d'ADCC", 'Mise en page de documents officiels : rapports annuels, plaquettes, attestations', 'Infographies pédagogiques pour la sensibilisation aux droits des travailleur·ses'],
    profil: "Maîtrise des logiciels de design (Illustrator, InDesign, Canva ou équivalents). Sens esthétique, créativité et rigueur graphique. Capacité à adapter son style aux différents supports et publics cibles. Intérêt pour les causes sociales et l'Afrique de l'Ouest.",
    dispo: 'Flexible'
  }, {
    role: 'Technico-commercial(e)',
    pole: 'Développement & insertion',
    mission: "Votre double expertise technique et commerciale est un levier stratégique pour ADCC. Vous bâtissez les ponts entre l'association et le monde économique, créant des opportunités concrètes pour les bénéficiaires et des ressources pour les programmes.",
    responsabilites: ["Prospection d'entreprises, hôtels, restaurants et familles susceptibles d'embaucher des bénéficiaires", "Présentation des formations et compétences d'ADCC aux employeurs potentiels", 'Négociation et suivi de conventions de partenariat et de placement', 'Développement de nouvelles sources de financement (mécénat, sponsoring, subventions)', 'Participation aux événements économiques et de networking à Cotonou et au Bénin', 'Contribution aux appels à projets et demandes de subventions'],
    profil: "Expérience en développement commercial, vente ou négociation. Excellent relationnel et aisance dans les milieux professionnels variés. Esprit d'initiative, autonomie et orientation résultats. Adhésion à l'économie sociale et solidaire et aux valeurs d'ADCC.",
    dispo: 'Régulière'
  }];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries()) as Record<string, string>;
    // Ajouter explicitement la charte acceptée
    data.charteAcceptee = charterAccepted ? 'Oui' : 'Non';
    // Supprimer le champ checkbox brut
    delete data.charter;
    // Sauvegarder dans la base de données locale
    saveSubmission<BenevoleSubmission>({
      type: 'benevole',
      nom: data.nom || '',
      prenom: '',
      email: data.email || '',
      telephone: data.telephone || '',
      quartier: data.quartier || '',
      disponibilite: data.disponibilite || '',
      motivation: `[Métier souhaité : ${data.metier}] ${data.motivation || ''}`
    });
    // Envoyer via Web3Forms (email à contact@adcc.africa)
    const result = await sendViaWeb3Forms({
      type: 'benevole',
      fields: data
    });
    console.log('📋 Bénévole submission result:', result);
    setIsLoading(false);
    setSubmitted(true);
    window.scrollTo(0, 0);
  };
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    scrollToSection('volunteer-form');
  };
  const toggleFiche = (role: string) => {
    setOpenFiche(openFiche === role ? null : role);
  };
  // --- 9. CONFIRMATION ---
  if (submitted) {
    return <Layout data-id="element-636">
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4 py-12" data-id="element-637">
          <Card className="max-w-md w-full text-center p-10 shadow-lg border-t-4 border-t-[#1E88E5]" data-id="element-638">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-639">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-640" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-641">
              Candidature envoyée !
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-642">
              Merci pour votre engagement. L'équipe ADCC a bien reçu votre
              profil et l'étudiera avec attention. Nous vous recontacterons très
              prochainement.
            </p>
            <Button onClick={() => window.location.reload()} className="w-full text-lg py-6" data-id="element-643">
              Fermer
            </Button>
          </Card>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-644">
      <SEO title="Devenir Bénévole | ADCC" description="Rejoignez le programme de bénévoles ADCC. Contribuez à nos actions au Bénin en mettant vos compétences au service de missions utiles." url="https://adcc.africa/benevoles" data-id="element-645" />

      {/* BLOC 1 — HERO */}
      <section className="bg-[#2C3E50] text-white py-20 md:py-28 relative overflow-hidden" data-id="element-646">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-647">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-648"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-649">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-6" data-id="element-650">
            Devenir Bénévole
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-651">
            Rejoignez le programme de bénévoles ADCC
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-652">
            Contribuez aux actions de l'ADCC au Bénin en mettant vos compétences
            au service de missions utiles.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-653">
            <Button size="lg" className="text-lg px-8 py-6 shadow-lg hover:scale-105 transition-transform" onClick={() => scrollToSection('volunteer-form')} data-id="element-654">
              Je postule
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10 hover:text-white" onClick={() => scrollToSection('poles')} data-id="element-655">
              Voir les métiers
            </Button>
          </div>
        </div>
      </section>

      {/* BLOC 2 — PÔLES & MÉTIERS */}
      <section id="poles" className="py-20 bg-gray-50" data-id="element-656">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-657">
          <div className="text-center mb-16" data-id="element-658">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-659">
              Métiers recherchés
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-660">
              Découvrez nos 4 pôles d'action et trouvez la mission qui vous
              correspond.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-661">
            {POLES.map(pole => <Card key={pole.id} className="border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden" data-id="element-662">
                {/* En-tête du pôle */}
                <div className={`p-6 border-b border-gray-100 ${pole.color}`} data-id="element-663">
                  <div className="flex items-center gap-4" data-id="element-664">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm" data-id="element-665">
                      <pole.icon className="w-6 h-6" data-id="element-666" />
                    </div>
                    <div data-id="element-667">
                      <h3 className="text-xl font-bold" data-id="element-668">{pole.title}</h3>
                      <p className="text-sm opacity-90" data-id="element-669">{pole.desc}</p>
                    </div>
                  </div>
                </div>

                {/* Liste des fiches métiers */}
                <div className="divide-y divide-gray-100" data-id="element-670">
                  {pole.roles.map(roleName => {
                const fiche = FICHES.find(f => f.role === roleName);
                if (!fiche) return null;
                const isOpen = openFiche === roleName;
                return <div key={roleName} className="bg-white" data-id="element-671">
                        {/* Bouton pour ouvrir/fermer la fiche */}
                        <button onClick={() => toggleFiche(roleName)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" data-id="element-672">
                          <span className="font-bold text-gray-900 text-lg" data-id="element-673">
                            {roleName}
                          </span>
                          {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-674" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-675" />}
                        </button>

                        {/* Contenu de la fiche (dépliable) */}
                        {isOpen && <div className="px-6 pb-6 pt-2 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-676">
                            <div className="space-y-6 bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-677">
                              {/* Mission */}
                              <div data-id="element-678">
                                <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2" data-id="element-679">
                                  <Target className="w-4 h-4 text-[#1E88E5]" data-id="element-680" />
                                  Mission
                                </h4>
                                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-681">
                                  {fiche.mission}
                                </p>
                              </div>

                              {/* Responsabilités */}
                              <div data-id="element-682">
                                <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2" data-id="element-683">
                                  <CheckSquare className="w-4 h-4 text-[#1E88E5]" data-id="element-684" />
                                  Responsabilités
                                </h4>
                                <ul className="space-y-2" data-id="element-685">
                                  {fiche.responsabilites.map((resp, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-686">
                                      <span className="text-[#1E88E5] mt-0.5" data-id="element-687">
                                        •
                                      </span>
                                      <span data-id="element-688">{resp}</span>
                                    </li>)}
                                </ul>
                              </div>

                              {/* Profil & Dispo */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200" data-id="element-689">
                                <div data-id="element-690">
                                  <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2" data-id="element-691">
                                    <Users className="w-4 h-4 text-[#1E88E5]" data-id="element-692" />
                                    Profil idéal
                                  </h4>
                                  <p className="text-gray-700 text-sm leading-relaxed" data-id="element-693">
                                    {fiche.profil}
                                  </p>
                                </div>
                                <div data-id="element-694">
                                  <h4 className="flex items-center gap-2 font-bold text-gray-900 mb-2" data-id="element-695">
                                    <Clock className="w-4 h-4 text-[#1E88E5]" data-id="element-696" />
                                    Disponibilité
                                  </h4>
                                  <p className="text-gray-700 text-sm font-medium bg-white px-3 py-1.5 rounded-md inline-block border border-gray-200" data-id="element-697">
                                    {fiche.dispo}
                                  </p>
                                </div>
                              </div>

                              {/* Bouton d'action */}
                              <div className="pt-4 flex justify-end" data-id="element-698">
                                <Button onClick={() => handleRoleSelect(roleName)} className="gap-2" data-id="element-699">
                                  Choisir ce rôle
                                  <ArrowRight className="w-4 h-4" data-id="element-700" />
                                </Button>
                              </div>
                            </div>
                          </div>}
                      </div>;
              })}
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* BLOC 3 — PROFIL DU BÉNÉVOLE */}
      <section className="py-20 bg-white" data-id="element-701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-703">
            <div className="order-2 lg:order-1 relative" data-id="element-704">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1E88E5]/20 to-transparent rounded-3xl transform -translate-x-6 translate-y-6" data-id="element-705"></div>
              <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800" alt="Bénévoles ADCC" className="relative rounded-3xl shadow-2xl w-full object-cover h-[500px]" data-id="element-706" />
            </div>

            <div className="order-1 lg:order-2" data-id="element-707">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-708">
                Le profil du bénévole ADCC
              </h2>
              <p className="text-xl text-gray-600 mb-10" data-id="element-709">
                Au-delà des compétences techniques, nous recherchons avant tout
                un état d'esprit.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-710">
                {[{
                title: 'Engagement',
                desc: "Adhésion sincère à la mission et aux valeurs de l'ADCC.",
                icon: Heart
              }, {
                title: 'Fiabilité',
                desc: 'Respect des engagements pris et ponctualité.',
                icon: Shield
              }, {
                title: 'Bienveillance',
                desc: "Attitude respectueuse envers les bénéficiaires et l'équipe.",
                icon: Users
              }, {
                title: 'Proactivité',
                desc: "Force de proposition et esprit d'initiative.",
                icon: TrendingUp
              }].map((item, index) => <div key={index} className="bg-gray-50 p-6 rounded-xl" data-id="element-711">
                    <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4" data-id="element-712">
                      <item.icon className="w-5 h-5 text-[#1E88E5]" data-id="element-713" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-714">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-715">{item.desc}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 4 — CE QUE LE BÉNÉVOLAT APPORTE */}
      <section className="py-20 bg-[#2C3E50] text-white" data-id="element-716">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-717">
          <div className="text-center mb-16" data-id="element-718">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-719">
              Ce que le bénévolat vous apporte
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-720">
              Un engagement mutuellement bénéfique.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-721">
            {['Expérience terrain concrète dans le secteur associatif', 'Développement de compétences professionnelles', 'Attestation officielle de bénévolat délivrée par ADCC', 'Intégration dans un réseau professionnel engagé', 'Contribution directe à une cause utile au Bénin', "Opportunité de carrière dans le secteur d'activité"].map((benefit, index) => <div key={index} className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/10" data-id="element-722">
                <CheckCircle2 className="w-6 h-6 text-[#1E88E5] flex-shrink-0" data-id="element-723" />
                <p className="text-gray-200 font-medium" data-id="element-724">{benefit}</p>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-725">
            <p className="inline-block bg-white/10 px-6 py-3 rounded-full text-sm text-blue-200" data-id="element-726">
              💡 Nous prévoyons également : Indemnités de transport, repas
              couverts lors des événements, et une flexibilité des horaires
              adaptée à vos contraintes.
            </p>
          </div>
        </div>
      </section>

      {/* BLOC 5 — FORMULAIRE */}
      <section id="volunteer-form" className="py-20 bg-gray-50" data-id="element-727">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-728">
          <Card className="shadow-xl border-t-4 border-t-[#1E88E5]" data-id="element-729">
            <CardContent className="p-8 md:p-12" data-id="element-730">
              <div className="text-center mb-10" data-id="element-731">
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-732">
                  Formulaire d'inscription
                </h2>
                <p className="text-gray-600" data-id="element-733">
                  Prêt(e) à nous rejoindre ? Remplissez vos informations
                  ci-dessous.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-id="element-734">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-735">
                  {/* Nom */}
                  <div className="space-y-2 md:col-span-2" data-id="element-736">
                    <label className="text-sm font-medium text-gray-700" data-id="element-737">
                      Nom complet *
                    </label>
                    <input type="text" name="nom" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all" placeholder="Votre nom et prénom" data-id="element-738" />
                  </div>

                  {/* Téléphone */}
                  <div className="space-y-2" data-id="element-739">
                    <label className="text-sm font-medium text-gray-700" data-id="element-740">
                      Téléphone *
                    </label>
                    <input type="tel" name="telephone" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all" placeholder="+229..." data-id="element-741" />
                  </div>

                  {/* Email */}
                  <div className="space-y-2" data-id="element-742">
                    <label className="text-sm font-medium text-gray-700" data-id="element-743">
                      Email *
                    </label>
                    <input type="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all" placeholder="votre@email.com" data-id="element-744" />
                  </div>

                  {/* Ville */}
                  <div className="space-y-2 md:col-span-2" data-id="element-745">
                    <label className="text-sm font-medium text-gray-700" data-id="element-746">
                      Ville / Commune *
                    </label>
                    <input type="text" name="quartier" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all" placeholder="Ex: Cotonou" data-id="element-747" />
                  </div>

                  {/* Métier souhaité */}
                  <div className="space-y-2" data-id="element-748">
                    <label className="text-sm font-medium text-gray-700" data-id="element-749">
                      Métier souhaité *
                    </label>
                    <select name="metier" required value={selectedRole} onChange={e => setSelectedRole(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white" data-id="element-750">
                      <option value="" data-id="element-751">Sélectionnez un rôle</option>
                      {FICHES.map(fiche => <option key={fiche.role} value={fiche.role} data-id="element-752">
                          {fiche.role}
                        </option>)}
                    </select>
                  </div>

                  {/* Disponibilité */}
                  <div className="space-y-2" data-id="element-753">
                    <label className="text-sm font-medium text-gray-700" data-id="element-754">
                      Disponibilités *
                    </label>
                    <select name="disponibilite" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all bg-white" data-id="element-755">
                      <option value="" data-id="element-756">Vos disponibilités ?</option>
                      <option value="ponctuelle" data-id="element-757">Ponctuelle</option>
                      <option value="weekends" data-id="element-758">Week-ends</option>
                      <option value="semaine" data-id="element-759">En semaine</option>
                      <option value="reguliere" data-id="element-760">Régulière</option>
                    </select>
                  </div>

                  {/* Motivation */}
                  <div className="space-y-2 md:col-span-2" data-id="element-761">
                    <label className="text-sm font-medium text-gray-700" data-id="element-762">
                      Motivation *
                    </label>
                    <textarea name="motivation" required rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none transition-all" placeholder="Décrivez brièvement pourquoi vous souhaitez rejoindre l'ADCC et ce rôle en particulier..." data-id="element-763"></textarea>
                  </div>

                  {/* Charte du bénévole */}
                  <div className="space-y-4 md:col-span-2 bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-764">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-765">
                      <Shield className="w-5 h-5 text-[#1E88E5]" data-id="element-766" />
                      Charte du bénévole ADCC
                    </h3>
                    <div className="h-32 overflow-y-auto text-sm text-gray-600 bg-white p-4 rounded border border-gray-200" data-id="element-767">
                      <p className="mb-2 font-bold" data-id="element-768">
                        En devenant bénévole pour l'ADCC, je m'engage à :
                      </p>
                      <ul className="list-disc pl-5 space-y-1" data-id="element-769">
                        <li data-id="element-770">
                          Adhérer pleinement à la mission et aux valeurs de
                          l'association (Dignité, Compétence, Création).
                        </li>
                        <li data-id="element-771">
                          Agir avec bienveillance, respect et sans aucune
                          discrimination envers les bénéficiaires, les
                          partenaires et les autres membres de l'équipe.
                        </li>
                        <li data-id="element-772">
                          Respecter la confidentialité absolue concernant les
                          informations personnelles des bénéficiaires et les
                          données internes de l'association.
                        </li>
                        <li data-id="element-773">
                          Faire preuve de fiabilité et de ponctualité dans les
                          missions qui me sont confiées.
                        </li>
                        <li data-id="element-774">
                          Informer l'équipe en cas d'empêchement dans les
                          meilleurs délais.
                        </li>
                        <li data-id="element-775">
                          Ne pas utiliser mon statut de bénévole à des fins
                          personnelles, commerciales ou politiques.
                        </li>
                        <li data-id="element-776">
                          Alerter immédiatement le bureau exécutif en cas de
                          situation d'abus ou de comportement inapproprié dont
                          je serais témoin.
                        </li>
                      </ul>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer mt-4" data-id="element-777">
                      <input type="checkbox" name="charter" required checked={charterAccepted} onChange={e => setCharterAccepted(e.target.checked)} className="mt-1 w-5 h-5 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5]" data-id="element-778" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-779">
                        J'ai lu et j'accepte la charte du bénévole ADCC *
                      </span>
                    </label>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-780">
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-781">
                    <span className="font-semibold text-gray-800" data-id="element-782">
                      📷 Droit à l'image :
                    </span>{' '}
                    En participant aux activités ADCC (formations, ateliers,
                    foires, événements ou toute autre activité), vous autorisez
                    ADCC à utiliser votre image (photos et vidéos) réalisées
                    lors de ces activités à des fins de communication
                    institutionnelle et promotionnelle sur le site adcc.africa
                    et les supports numériques et imprimés associés. Cette
                    autorisation peut être retirée à tout moment par demande
                    écrite adressée à ADCC, sans effet rétroactif sur les
                    contenus déjà publiés.
                  </p>
                </div>

                <Button type="submit" className="w-full text-lg py-6 shadow-lg" disabled={isLoading || !charterAccepted} data-id="element-783">
                  {isLoading ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>;
}